import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHeader } from '../design';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { 
  TrendingUp,
  TrendingDown,
  Building2,
  Users,
  Bot,
  DollarSign,
  Activity,
  Shield,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Loader2,
  Eye,
  Beaker,
  CheckCircle,
  Lock,
  Download,
  Calendar,
  BarChart3,
  PieChart,
  Percent,
  Target,
  Zap,
  CreditCard,
  UserCheck,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Skeleton } from '../../../components/ui/skeleton';

/**
 * InvestorView - Enhanced Read-only Dashboard for Investor Presentations
 * 
 * Features:
 * - Real MRR/ARR calculations from billing data
 * - Dynamic growth rates from historical data
 * - Churn rate calculations
 * - AI performance metrics
 * - Cost efficiency metrics
 * - Trend visualizations (12-month charts)
 * - Time period selection (7d, 30d, 90d, 1y)
 * - Export functionality (CSV, JSON)
 * - Auto-refresh option
 * 
 * SECURITY:
 * - Strictly read-only (no action buttons)
 * - Uses aggregated/anonymized metrics
 * - Safe for screen-sharing
 */
export default function InvestorView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await governanceAPI.getEnhancedInvestorDashboard(timeRange);
      setData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching investor data:', err);
      setError('Failed to load investor dashboard');
      toast.error('Failed to load investor dashboard');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Export handler
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await governanceAPI.exportInvestorReport(format);
      const blob = new Blob([response.data], { 
        type: format === 'json' ? 'application/json' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `investor_report_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Format large numbers
  const formatNumber = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value?.toLocaleString() || '0';
  };

  // Growth indicator
  const GrowthIndicator = ({ value, suffix = '%' }) => {
    const isPositive = value >= 0;
    return (
      <span className={cn(
        "flex items-center text-sm font-medium",
        isPositive ? "text-emerald-500" : "text-red-500"
      )}>
        {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
        {isPositive ? '+' : ''}{value?.toFixed(1)}{suffix}
      </span>
    );
  };

  // Trend chart max value
  const getTrendMax = (data, key) => {
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map(d => d[key] || 0), 1);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        <span className="ml-2 text-slate-400">Loading investor dashboard...</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="investor-view">
        {/* Header */}
        <PageHeader
          title="Investor Dashboard"
          subtitle="Real-time platform metrics & analytics"
          actions={
            <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-green-300 dark:border-green-500/50 text-green-600 dark:text-green-400">
              <Shield className="h-3 w-3 mr-1" />
              Safe to Share
            </Badge>
            <Badge variant="outline" className="border-amber-300 dark:border-amber-500/50 text-amber-600 dark:text-amber-400">
              <Lock className="h-3 w-3 mr-1" />
              Read-Only
            </Badge>
            
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[130px]" data-testid="time-range-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
              </SelectContent>
            </Select>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={autoRefresh ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  data-testid="auto-refresh-btn"
                >
                  <RefreshCw className={cn("h-4 w-4", autoRefresh && "animate-spin")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {autoRefresh ? 'Auto-refresh ON (60s)' : 'Enable auto-refresh'}
              </TooltipContent>
            </Tooltip>
            
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={exporting}
                data-testid="export-csv-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('json')}
                disabled={exporting}
                data-testid="export-json-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                JSON
              </Button>
            </div>
          </div>
          }
        />

        {/* Security Notice */}
        <Alert className="border-green-300 dark:border-green-600/30 bg-green-50 dark:bg-green-950/20">
          <Shield className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertTitle className="text-green-700 dark:text-green-300">Investor View (Read-Only)</AlertTitle>
          <AlertDescription className="text-green-600 dark:text-green-400/80">
            Real-time aggregated metrics from live platform data. Safe for presentations.
          </AlertDescription>
        </Alert>

        {error ? (
          <Card className="bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
            <CardContent className="pt-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <Button onClick={fetchData} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Platform Tagline */}
            <Card className="bg-gradient-to-br from-amber-100 dark:from-amber-900/30 to-orange-100 dark:to-orange-900/30 border-amber-200 dark:border-amber-500/30">
              <CardContent className="pt-6">
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                    Lockene AI
                  </h2>
                  <p className="text-xl text-amber-700 dark:text-amber-200">
                    Enterprise AI Governance Platform
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-slate-100 dark:bg-slate-800 grid grid-cols-4 w-full max-w-xl">
                <TabsTrigger value="overview" className="gap-1">
                  <BarChart3 className="h-4 w-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="revenue" className="gap-1">
                  <DollarSign className="h-4 w-4" />
                  Revenue
                </TabsTrigger>
                <TabsTrigger value="growth" className="gap-1">
                  <TrendingUp className="h-4 w-4" />
                  Growth
                </TabsTrigger>
                <TabsTrigger value="ai" className="gap-1">
                  <Bot className="h-4 w-4" />
                  AI Metrics
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Organizations
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="total-orgs">
                          {data?.overview?.total_organizations || 0}
                        </span>
                        <GrowthIndicator value={data?.growth_metrics?.org_growth_rate || 0} />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {data?.overview?.active_organizations || 0} active
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Tenants
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="total-tenants">
                          {data?.overview?.total_tenants || 0}
                        </span>
                        <GrowthIndicator value={data?.growth_metrics?.tenant_growth_rate || 0} />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {data?.overview?.production_tenants || 0} production, {data?.overview?.demo_tenants || 0} demo
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        Users
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="total-users">
                          {data?.overview?.total_users || 0}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {data?.overview?.active_users || 0} active
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                        <Bot className="h-4 w-4" />
                        AI Agents
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="total-agents">
                          {data?.overview?.total_agents || 0}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {data?.ai_metrics?.utilization_rate || 0}% utilization
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue Highlights */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Monthly Recurring Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="mrr">
                          {formatCurrency(data?.revenue_metrics?.mrr)}
                        </span>
                      </div>
                      <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70 mt-1">
                        {data?.revenue_metrics?.active_subscriptions || 0} active subscriptions
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Annual Recurring Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-blue-600 dark:text-blue-400" data-testid="arr">
                          {formatCurrency(data?.revenue_metrics?.arr)}
                        </span>
                      </div>
                      <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-1">
                        Avg ${data?.revenue_metrics?.avg_revenue_per_tenant || 0}/tenant
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Retention Rate
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-purple-600 dark:text-purple-400" data-testid="retention">
                          {data?.churn_metrics?.retention_rate || 100}%
                        </span>
                      </div>
                      <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-1">
                        {data?.churn_metrics?.churn_rate || 0}% churn rate
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tenant Growth Trend */}
                <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      Tenant Growth (12 Months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-1 h-32" data-testid="tenant-trend-chart">
                      {(data?.trends?.tenants || []).map((month, i) => {
                        const max = getTrendMax(data?.trends?.tenants, 'count');
                        const height = Math.max((month.count / max) * 100, 4);
                        return (
                          <Tooltip key={i}>
                            <TooltipTrigger asChild>
                              <div
                                className="flex-1 bg-emerald-500/80 hover:bg-emerald-500 rounded-t transition-all cursor-pointer"
                                style={{ height: `${height}%` }}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{month.label}</p>
                              <p className="text-xs text-slate-400">{month.count} new tenants</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      {(data?.trends?.tenants || []).filter((_, i) => i % 3 === 0).map((month, i) => (
                        <span key={i}>{month.label}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Revenue Tab */}
              <TabsContent value="revenue" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-emerald-500" />
                        MRR
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <span className="text-3xl font-bold text-emerald-500">
                        {formatCurrency(data?.revenue_metrics?.mrr)}
                      </span>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-blue-500" />
                        ARR
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <span className="text-3xl font-bold text-blue-500">
                        {formatCurrency(data?.revenue_metrics?.arr)}
                      </span>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-amber-500" />
                        Period Revenue
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-amber-500">
                          {formatCurrency(data?.revenue_metrics?.total_revenue_period)}
                        </span>
                        <GrowthIndicator value={data?.growth_metrics?.revenue_growth_rate || 0} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-500" />
                        ARPU
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <span className="text-3xl font-bold text-purple-500">
                        {formatCurrency(data?.revenue_metrics?.avg_revenue_per_tenant)}
                      </span>
                      <p className="text-xs text-slate-500 mt-1">per tenant/month</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue Trend */}
                <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-emerald-500" />
                      Revenue Trend (12 Months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-1 h-40" data-testid="revenue-trend-chart">
                      {(data?.trends?.revenue || []).map((month, i) => {
                        const max = getTrendMax(data?.trends?.revenue, 'revenue');
                        const height = Math.max((month.revenue / max) * 100, 4);
                        return (
                          <Tooltip key={i}>
                            <TooltipTrigger asChild>
                              <div
                                className="flex-1 bg-gradient-to-t from-emerald-500 to-emerald-400 hover:from-emerald-600 hover:to-emerald-500 rounded-t transition-all cursor-pointer"
                                style={{ height: `${height}%` }}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{month.label}</p>
                              <p className="text-xs text-slate-400">{formatCurrency(month.revenue)}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      {(data?.trends?.revenue || []).filter((_, i) => i % 3 === 0).map((month, i) => (
                        <span key={i}>{month.label}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Breakdown */}
                <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <PieChart className="h-4 w-4 text-blue-500" />
                      Platform Cost Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">
                          {formatCurrency(data?.cost_metrics?.total_platform_cost)}
                        </p>
                        <p className="text-xs text-slate-500">Total Cost</p>
                      </div>
                      <div className="text-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                          {formatCurrency(data?.cost_metrics?.llm_cost)}
                        </p>
                        <p className="text-xs text-slate-500">LLM</p>
                      </div>
                      <div className="text-center p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                          {formatCurrency(data?.cost_metrics?.twilio_cost)}
                        </p>
                        <p className="text-xs text-slate-500">Twilio</p>
                      </div>
                      <div className="text-center p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                          {formatCurrency(data?.cost_metrics?.elevenlabs_cost)}
                        </p>
                        <p className="text-xs text-slate-500">ElevenLabs</p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-between">
                      <span className="text-sm text-emerald-700 dark:text-emerald-300">
                        Cost Efficiency Ratio
                      </span>
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {data?.cost_metrics?.cost_efficiency_ratio || 0}x
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Growth Tab */}
              <TabsContent value="growth" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Org Growth
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className={cn(
                          "text-3xl font-bold",
                          (data?.growth_metrics?.org_growth_rate || 0) >= 0 ? "text-emerald-500" : "text-red-500"
                        )}>
                          {data?.growth_metrics?.org_growth_rate >= 0 ? '+' : ''}{data?.growth_metrics?.org_growth_rate || 0}%
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        +{data?.growth_metrics?.new_orgs_period || 0} this period
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Tenant Growth
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className={cn(
                          "text-3xl font-bold",
                          (data?.growth_metrics?.tenant_growth_rate || 0) >= 0 ? "text-emerald-500" : "text-red-500"
                        )}>
                          {data?.growth_metrics?.tenant_growth_rate >= 0 ? '+' : ''}{data?.growth_metrics?.tenant_growth_rate || 0}%
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        +{data?.growth_metrics?.new_tenants_period || 0} this period
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Revenue Growth
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className={cn(
                          "text-3xl font-bold",
                          (data?.growth_metrics?.revenue_growth_rate || 0) >= 0 ? "text-emerald-500" : "text-red-500"
                        )}>
                          {data?.growth_metrics?.revenue_growth_rate >= 0 ? '+' : ''}{data?.growth_metrics?.revenue_growth_rate || 0}%
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Percent className="h-4 w-4" />
                        Churn Rate
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-baseline gap-2">
                        <span className={cn(
                          "text-3xl font-bold",
                          (data?.churn_metrics?.churn_rate || 0) <= 5 ? "text-emerald-500" : "text-amber-500"
                        )}>
                          {data?.churn_metrics?.churn_rate || 0}%
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {data?.churn_metrics?.churned_tenants || 0} churned
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Organization Growth Trend */}
                <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-500" />
                      Organization Growth (12 Months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-1 h-32" data-testid="org-trend-chart">
                      {(data?.trends?.organizations || []).map((month, i) => {
                        const max = getTrendMax(data?.trends?.organizations, 'count');
                        const height = Math.max((month.count / max) * 100, 4);
                        return (
                          <Tooltip key={i}>
                            <TooltipTrigger asChild>
                              <div
                                className="flex-1 bg-blue-500/80 hover:bg-blue-500 rounded-t transition-all cursor-pointer"
                                style={{ height: `${height}%` }}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{month.label}</p>
                              <p className="text-xs text-slate-400">{month.count} new orgs</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                      {(data?.trends?.organizations || []).filter((_, i) => i % 3 === 0).map((month, i) => (
                        <span key={i}>{month.label}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* AI Metrics Tab */}
              <TabsContent value="ai" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-500" />
                        AI Executions
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <span className="text-3xl font-bold text-amber-500" data-testid="ai-executions">
                        {formatNumber(data?.ai_metrics?.total_executions)}
                      </span>
                      <p className="text-xs text-slate-500 mt-1">in selected period</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Success Rate
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <span className="text-3xl font-bold text-emerald-500">
                          {data?.ai_metrics?.success_rate || 0}%
                        </span>
                        <Progress value={data?.ai_metrics?.success_rate || 0} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-indigo-500" />
                        Active Agent Types
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <span className="text-3xl font-bold text-indigo-500">
                        {data?.ai_metrics?.active_agent_types || 0}
                      </span>
                      <p className="text-xs text-slate-500 mt-1">different types</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-purple-500" />
                        Utilization
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <span className="text-3xl font-bold text-purple-500">
                          {data?.ai_metrics?.utilization_rate || 0}%
                        </span>
                        <Progress value={data?.ai_metrics?.utilization_rate || 0} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* AI Usage Trend */}
                <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4 text-amber-500" />
                      AI Usage Trend (30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-[2px] h-32" data-testid="ai-usage-trend">
                      {(data?.trends?.ai_usage || []).map((day, i) => {
                        const max = getTrendMax(data?.trends?.ai_usage, 'executions');
                        const height = Math.max((day.executions / max) * 100, 4);
                        return (
                          <Tooltip key={i}>
                            <TooltipTrigger asChild>
                              <div
                                className="flex-1 bg-amber-500/80 hover:bg-amber-500 rounded-t transition-all cursor-pointer min-h-[4px]"
                                style={{ height: `${height}%` }}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{day.label}</p>
                              <p className="text-xs text-slate-400">{day.executions} executions</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Savings */}
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-700">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Estimated Cost Savings
                    </CardTitle>
                    <CardDescription className="text-emerald-600/70 dark:text-emerald-400/70">
                      Based on AI automation vs manual processing
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <span className="text-5xl font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(data?.cost_metrics?.estimated_savings)}
                      </span>
                      <p className="text-sm text-emerald-600/70 dark:text-emerald-400/70 mt-2">
                        Estimated monthly savings from AI automation
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Key Features */}
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-900 dark:text-white flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  Key Platform Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    'Multi-tenant AI governance',
                    'Kill switch controls',
                    'Budget tracking & alerts',
                    'Confidence-based auto-pause',
                    'Complete audit trail',
                    'Real-time monitoring'
                  ].map((feature, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg"
                    >
                      <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Footer */}
            <div className="text-center text-slate-500 text-sm border-t border-slate-200 dark:border-slate-800 pt-4">
              <p className="flex items-center justify-center gap-2">
                <Lock className="h-3 w-3" />
                Investor View (Read-Only) • Last updated: {lastRefresh.toLocaleString()}
              </p>
              <p className="mt-1 text-slate-400 dark:text-slate-600">
                Safe for screen-sharing. No actions can be performed.
              </p>
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
